import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { LabsPublishedList, LabsReviewProvider, LabsReviewQueue, type LabsReviewModule } from '@ludolio/shared-labs';
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Textarea,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@ludolio/shared-ui';
import { getApiErrorMessage } from '@/lib/api-error';
import { labsQuizNoticeApi, labsReviewApi, labsSettingsApi, labsTeamAdminApi, type LabQuizNotice, type OrphanedLabTeam } from '@/services/api';
import type { LabsSettings } from '@/types';

type Visibility = LabsSettings['defaultVisibility'];

const VISIBILITY_OPTIONS: Array<{ value: Visibility; label: string }> = [
  { value: 'organization', label: 'University' },
  { value: 'unit', label: 'Unit' },
  { value: 'private', label: 'Project team' },
];

function formatGiB(bytes: number): string {
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export const Route = createFileRoute('/_authenticated/labs')({
  component: LabsPage,
});

function LabsPage() {
  const [settings, setSettings] = useState<LabsSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const reviewModule = useMemo<LabsReviewModule>(() => ({
    api: labsReviewApi,
    getErrorMessage: (err) => getApiErrorMessage(err, 'Something went wrong. Please try again.'),
  }), []);

  useEffect(() => {
    labsSettingsApi
      .get()
      .then(setSettings)
      .catch((err) => setError(getApiErrorMessage(err, 'Failed to load Labs settings')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading Labs…</div>;
  if (settings && !settings.enabled) return <Navigate to="/dashboard" replace />;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <h1 className="text-2xl font-bold">Labs</h1>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {!settings && !error && <p className="text-sm text-muted-foreground">Labs is not available in demo mode.</p>}

      {settings && (
        <LabsReviewProvider module={reviewModule}>
          <Tabs defaultValue="reviews">
            <TabsList>
              <TabsTrigger value="reviews">Reviews</TabsTrigger>
              <TabsTrigger value="published">Published</TabsTrigger>
              <TabsTrigger value="teams">Teams</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
            <TabsContent value="reviews" className="mt-6">
              <LabsReviewQueue scope="organization" emptyMessage="Nothing is waiting for review" />
            </TabsContent>
            <TabsContent value="published" className="mt-6">
              <LabsPublishedList />
            </TabsContent>
            <TabsContent value="teams" className="mt-6">
              <OrphanedTeams />
            </TabsContent>
            <TabsContent value="settings" className="mt-6 space-y-6">
              <LabsSettingsForm settings={settings} onSaved={setSettings} />
              <QuizNoticeForm />
            </TabsContent>
          </Tabs>
        </LabsReviewProvider>
      )}
    </div>
  );
}

function LabsSettingsForm({ settings, onSaved }: { settings: LabsSettings; onSaved: (settings: LabsSettings) => void }) {
  const [defaultVisibility, setDefaultVisibility] = useState<Visibility>(settings.defaultVisibility);
  const [reportContactEmail, setReportContactEmail] = useState(settings.reportContactEmail ?? '');
  const [remixingEnabled, setRemixingEnabled] = useState(settings.remixingEnabled);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [saving, setSaving] = useState(false);

  const termsAccepted = Boolean(settings.internalRemixTerms.acceptedAt);
  const mustAcceptTerms = remixingEnabled && !termsAccepted;
  const dirty = defaultVisibility !== settings.defaultVisibility
    || reportContactEmail.trim() !== (settings.reportContactEmail ?? '')
    || remixingEnabled !== settings.remixingEnabled
    || (mustAcceptTerms && acceptTerms);
  const usagePercent = settings.storageQuotaBytes > 0
    ? Math.round((settings.storageUsedBytes / settings.storageQuotaBytes) * 100)
    : 0;

  const save = async () => {
    setSaving(true);
    try {
      const next = await labsSettingsApi.update({
        defaultVisibility,
        reportContactEmail: reportContactEmail.trim() || null,
        remixingEnabled,
        ...(mustAcceptTerms ? { acceptInternalRemixTerms: true } : {}),
      });
      setAcceptTerms(false);
      onSaved(next);
      toast.success('Labs settings saved');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save Labs settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="space-y-6 rounded-lg border border-border bg-card p-5"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
        <span className="text-sm font-medium">Labs address</span>
        {settings.labsUrl ? (
          <a href={settings.labsUrl} target="_blank" rel="noopener noreferrer" className="truncate text-sm underline">
            {settings.labsUrl}
          </a>
        ) : (
          <span className="text-sm text-muted-foreground">Not set yet. Contact Ludolio to set your Labs address.</span>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
        <Label htmlFor="labs-default-visibility">Default audience</Label>
        <Select value={defaultVisibility} onValueChange={(value) => setDefaultVisibility(value as Visibility)} disabled={saving}>
          <SelectTrigger id="labs-default-visibility" className="sm:w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VISIBILITY_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
        <Label htmlFor="labs-report-contact">Report contact</Label>
        <Input
          id="labs-report-contact"
          type="email"
          className="sm:w-80"
          placeholder="labs@university.edu"
          value={reportContactEmail}
          onChange={(event) => setReportContactEmail(event.target.value)}
          disabled={saving}
        />
      </div>

      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
        <Label htmlFor="labs-remixing">Allow remixing</Label>
        <Switch
          id="labs-remixing"
          checked={remixingEnabled}
          onCheckedChange={setRemixingEnabled}
          disabled={saving}
        />
      </div>

      {mustAcceptTerms && (
        <div className="grid gap-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <span className="text-sm font-medium">Internal remix terms</span>
          <div className="space-y-3 sm:max-w-xl">
            <p className="whitespace-pre-line text-sm text-muted-foreground">
              Version {settings.internalRemixTerms.version}. {settings.internalRemixTerms.text}
            </p>
            <div className="flex items-start gap-3">
              <Checkbox id="labs-accept-terms" checked={acceptTerms} onCheckedChange={(value) => setAcceptTerms(value === true)} disabled={saving} />
              <Label htmlFor="labs-accept-terms" className="font-normal leading-5">
                Accept internal remix terms, version {settings.internalRemixTerms.version}
              </Label>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
        <span className="text-sm font-medium">Storage</span>
        <div className="space-y-2 sm:w-80">
          <Progress value={usagePercent} aria-label="Labs storage used" />
          <p className="text-xs text-muted-foreground">
            {formatGiB(settings.storageUsedBytes)} of {formatGiB(settings.storageQuotaBytes)} used
          </p>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={!dirty || saving || (mustAcceptTerms && !acceptTerms)}>
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </div>
    </form>
  );
}

function QuizNoticeForm() {
  const [notice, setNotice] = useState<LabQuizNotice | null>(null);
  const [body, setBody] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    labsQuizNoticeApi.get()
      .then((current) => {
        setNotice(current);
        setBody(current?.body ?? '');
        setDocumentUrl(current?.documentUrl ?? '');
        setReady(true);
      })
      .catch((err) => setError(getApiErrorMessage(err, 'Failed to load the quiz notice')));
  }, []);

  const dirty = body.trim() !== (notice?.body ?? '') || documentUrl.trim() !== (notice?.documentUrl ?? '');

  const publish = async () => {
    setSaving(true);
    try {
      const next = await labsQuizNoticeApi.publish({
        body: body.trim() || null,
        documentUrl: documentUrl.trim() || null,
      });
      setNotice(next);
      setBody(next.body ?? '');
      setDocumentUrl(next.documentUrl ?? '');
      toast.success('Quiz notice published');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to publish the quiz notice'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="space-y-6 rounded-lg border border-border bg-card p-5"
      onSubmit={(event) => {
        event.preventDefault();
        void publish();
      }}
    >
      <div className="space-y-1">
        <h2 className="text-sm font-semibold">Quiz notice</h2>
        <p className="text-sm text-muted-foreground">
          People agree to this once. A new version asks them to agree again.
          {notice ? ` Current version ${notice.version}.` : ''}
        </p>
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)]">
        <Label htmlFor="quiz-notice-body" className="sm:pt-2">Notice</Label>
        <Textarea
          id="quiz-notice-body"
          rows={5}
          maxLength={8000}
          value={body}
          disabled={!ready || saving}
          onChange={(event) => setBody(event.target.value)}
        />
      </div>
      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
        <Label htmlFor="quiz-notice-url">Link</Label>
        <Input
          id="quiz-notice-url"
          type="url"
          className="sm:max-w-xl"
          placeholder="https://"
          value={documentUrl}
          disabled={!ready || saving}
          onChange={(event) => setDocumentUrl(event.target.value)}
        />
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={!ready || saving || !dirty || (!body.trim() && !documentUrl.trim())}>
          {saving ? 'Publishing…' : 'Publish notice'}
        </Button>
      </div>
    </form>
  );
}

const ROLE_LABELS = { owner: 'Owner', manager: 'Manager', member: 'Member' } as const;

function OrphanedTeams() {
  const [page, setPage] = useState(1);
  const [teams, setTeams] = useState<OrphanedLabTeam[] | null>(null);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [error, setError] = useState('');
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = (nextPage: number) => {
    labsTeamAdminApi
      .listOrphaned(nextPage)
      .then((result) => {
        setTeams(result.teams);
        setTotal(result.total);
        setPageSize(result.pageSize);
        setError('');
      })
      .catch((err) => setError(getApiErrorMessage(err, 'Failed to load teams')));
  };

  useEffect(() => {
    load(page);
  }, [page]);

  const assign = async (teamId: string) => {
    const email = emails[teamId]?.trim();
    if (!email) return;
    setBusyId(teamId);
    try {
      await labsTeamAdminApi.assignOwner(teamId, email);
      toast.success('Owner assigned');
      setEmails((current) => ({ ...current, [teamId]: '' }));
      load(page);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to assign an owner'));
    } finally {
      setBusyId(null);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  if (!teams && !error) return <p className="text-sm text-muted-foreground">Loading teams…</p>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Teams without an owner</h2>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {teams?.length === 0 && <p className="text-sm text-muted-foreground">Every team has an owner.</p>}
      {teams && teams.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {teams.map((team) => (
            <li key={team.id} className="space-y-4 p-4">
              <div className="min-w-0">
                <h3 className="truncate font-medium">{team.name}</h3>
                <p className="truncate text-sm text-muted-foreground">
                  {team.projectTotal === 0
                    ? 'No projects'
                    : team.projects.map((project) => project.title).join(', ')}
                  {team.projectTotal > team.projects.length ? ` · ${team.projectTotal} projects` : ''}
                </p>
              </div>
              <ul className="space-y-2">
                {team.members.map((member) => (
                  <li key={member.userId} className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{member.name}</p>
                      <p className="truncate text-sm text-muted-foreground">{member.email}</p>
                    </div>
                    <Badge variant="secondary">{ROLE_LABELS[member.role]}</Badge>
                    <div className="w-20 text-right">
                      {!member.inOrganization && <Badge variant="outline">Outside</Badge>}
                    </div>
                  </li>
                ))}
              </ul>
              <form
                className="flex flex-col gap-3 sm:flex-row"
                onSubmit={(event) => {
                  event.preventDefault();
                  void assign(team.id);
                }}
              >
                <Label htmlFor={`owner-${team.id}`} className="sr-only">New owner email</Label>
                <Input
                  id={`owner-${team.id}`}
                  type="email"
                  placeholder="name@university.edu"
                  value={emails[team.id] ?? ''}
                  onChange={(event) => setEmails((current) => ({ ...current, [team.id]: event.target.value }))}
                  disabled={busyId === team.id}
                />
                <Button type="submit" disabled={busyId === team.id || !emails[team.id]?.trim()} className="sm:w-36">
                  {busyId === team.id ? 'Assigning…' : 'Assign owner'}
                </Button>
              </form>
            </li>
          ))}
        </ul>
      )}
      {pageCount > 1 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
